import { useState, useEffect, useMemo } from "react";
import { normalizeDateFromAPI } from "../../utils/applicationFormatters";
import {
  verifyEmail,
  sendApplicationLink,
  getPreviousApplicationList,
} from "../../services/applicationService";
import { showSuccess, showError, showInfo } from "@/lib/utils/notifications";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import {
  formatApplicationRentalStatusLine,
  hasApplicationDefaultLink,
  hasApplicationResidentLink,
  isApplicationPrimaryApplicant,
} from "../../utils/applicationResidentHandoff";
import {
  openReimbursementsListForApplicant,
  openResidentListForApplicant,
} from "@/lib/utils/residentNavigation";
import { AboutYourselfEditView } from "./about-yourself/AboutYourselfEditView";
import { AboutYourselfReadOnlyView } from "./about-yourself/AboutYourselfReadOnlyView";
import {
  applyPreviousAddressMonths,
  applyValidateAddress,
  createEmptyPreviousAddressRow,
  normalizePreviousAddressRows,
  resolvePreviousFormsForPipeline,
  runCurrentAddressAddressPipeline,
} from "../../utils/previousAddressLogic";
import {
  ADDRESS_MAP_LINK_ROLES,
  EMAIL_VALIDATE_ROLES,
  buildAboutYourselfFormChangePayload,
  createEmptyAlternateAddressForm,
  hasNonUSAddress as hasNonUSAddressInHistory,
  mapAlternateAddressesToForms,
  mapApiAddressToPreviousAddressRow,
} from "./aboutYourselfTabHelpers";
import type {
  AboutYourselfTabProps,
  AboutYourselfPersonalFormData,
  AboutYourselfAddressEditFormData,
  AboutYourselfAlternateAddressFormData,
} from '../../types/tabs'
import type {
  AddressLike,
  ApplyValidateAddressOptions,
  PreviousAddressRow,
} from '../../types/utils'
import {
  aboutYourselfPersonalDisplay,
  aboutYourselfPersonalUserData,
  hasPersonalInfoSnapshot,
} from '../../utils/personalInfoSnapshot'


export function AboutYourselfTab({
  applicationAllData,
  applicationData,
  applicantId,
  isEdit = false,
  onFormChange,
  exposeInlineValidation = false,
  view = "personal",
}: AboutYourselfTabProps) {
  const user = useAppSelector(selectUser);
  const currentUserRole = user?.current_role?.role_name || "";
  const canValidateEmail = EMAIL_VALIDATE_ROLES.includes(currentUserRole);
  const canViewAddressOnMap = ADDRESS_MAP_LINK_ROLES.includes(currentUserRole);

  const userData = applicationData?.user_data || {};
  const personalDisplay = useMemo(
    () => aboutYourselfPersonalDisplay(applicationData),
    [applicationData],
  );
  const snapshotUserData = useMemo(
    () => aboutYourselfPersonalUserData(applicationData),
    [applicationData],
  );
  const personalFieldsLocked = hasPersonalInfoSnapshot(applicationData);
  const applicationDataForPersonal = useMemo(
    () =>
      personalFieldsLocked
        ? {
            ...applicationData,
            birthdate: personalDisplay.birthdate,
            security_number: personalDisplay.security_number,
            communication_preference: personalDisplay.communication_preference,
          }
        : applicationData,
    [applicationData, personalDisplay, personalFieldsLocked],
  );
  const showPrimaryApplicantActions = isApplicationPrimaryApplicant(applicationData);
  const showResidentButton = hasApplicationResidentLink(applicationAllData);
  const showDefaultButton = hasApplicationDefaultLink(applicationAllData);
  const rentalStatusLine = formatApplicationRentalStatusLine(applicationAllData);
  const currentAddress = applicationData?.current_address;
  const previousAddresses = applicationData?.previous_address || [];
  const alternateAddresses = applicationData?.alternate_address || [];

  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [sendingLink, setSendingLink] = useState<"email" | "sms" | null>(null);
  const [previousAppsOpen, setPreviousAppsOpen] = useState(false);
  const [previousAppsCount, setPreviousAppsCount] = useState<number | null>(null);
  /** Replaces window.confirm for Verify Email — stores the pending email until user confirms. */
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState<string | null>(null);
  /** Replaces window.confirm for Send Applicant Link — stores the pending channel. */
  const [pendingSendLink, setPendingSendLink] = useState<"email" | "sms" | null>(null);
  /** Once a personal field is edited, show inline errors for that field (Angular FormControl.invalid + dirty). */
  const [personalFieldsDirty, setPersonalFieldsDirty] = useState<
    Record<string, boolean>
  >({});
  /** Angular `(mouseenter)` / `(mouseleave)` on fa-info-circle next to Communication Preference */
  const [communicationPreferenceHelpOpen, setCommunicationPreferenceHelpOpen] =
    useState(false);

  const handleOpenResident = () => {
    try {
      openResidentListForApplicant(applicantId);
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : "Unable to open resident list.");
    }
  };

  const handleOpenDefault = () => {
    try {
      openReimbursementsListForApplicant(applicantId);
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : "Unable to open defaults list.");
    }
  };

  // Handle verify email — opens ConfirmDialog; verify runs on confirm.
  const handleVerifyEmail = (email: string) => {
    if (!email) return;
    setPendingVerifyEmail(email);
  };

  const performVerifyEmail = async () => {
    const email = pendingVerifyEmail;
    setPendingVerifyEmail(null);
    if (!email) return;
    setVerifyingEmail(true);
    try {
      const result = await verifyEmail(email);
      showInfo(result || "Email Verify Status");
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === "string" && msg ? msg : "Failed to verify email");
    } finally {
      setVerifyingEmail(false);
    }
  };

  // Handle send application link — opens ConfirmDialog; send runs on confirm.
  const handleSendApplicationLink = (type: "email" | "sms") => {
    setPendingSendLink(type);
  };

  const performSendApplicationLink = async () => {
    const type = pendingSendLink;
    setPendingSendLink(null);
    if (!type) return;
    setSendingLink(type);
    try {
      await sendApplicationLink(applicantId, type);
      showSuccess("Applicant Link Send Successfully!");
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === "string" && msg ? msg : "Failed to send application link");
    } finally {
      setSendingLink(null);
    }
  };

  // YYYY-MM-DD for date inputs; shared normalizer handles API ISO strings.
  const parseDate = (dateValue: unknown): string => {
    if (!dateValue) return "";
    if (typeof dateValue === "string") {
      const normalized = normalizeDateFromAPI(dateValue);
      if (normalized) return normalized;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
        const [day, month, year] = dateValue.split("/");
        return `${year}-${month}-${day}`;
      }
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
        const [month, day, year] = dateValue.split("-");
        return `${year}-${month}-${day}`;
      }
    }
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const getAddressMoveInYmd = (
    addr: PreviousAddressRow | AddressLike | null | undefined,
  ): string => {
    return parseDate(addr?.moveIn_date || addr?.address_data?.moveIn_date || "");
  };

  // Form state for edit mode - personal info
  const [formData, setFormData] = useState<AboutYourselfPersonalFormData>({
    first_name: userData.first_name || "",
    middle_name: userData.middle_name || "",
    last_name: userData.last_name || "",
    suffix: userData.suffix || "",
    mobile_no: userData.mobile_no || "",
    shared_phone_no: userData.shared_phone_no === "1",
    communication_preference:
      applicationData?.communication_preference || "Text Msg",
    birthdate: parseDate(applicationData?.birthdate),
    security_number: applicationData?.security_number || "",
    email: userData.email || "",
  });

  // Address form state
  const [currentAddressForm, setCurrentAddressForm] =
    useState<PreviousAddressRow | null>(null);
  const [previousAddressForms, setPreviousAddressForms] = useState<
    PreviousAddressRow[]
  >([]);
  // "I don't have an alternate address in the US" checkbox — Angular `nonUSAddress` /
  // `validateAllUSAddress` (current outside US and all previous outside US).
  const [noAlternateAddressUS, setNoAlternateAddressUS] = useState(
    applicationData?.non_us_address === "1" || false,
  );
  const [alternateAddressForms, setAlternateAddressForms] = useState<
    AboutYourselfAlternateAddressFormData[]
  >(() => mapAlternateAddressesToForms(alternateAddresses));
  /**
   * ra_ids of saved previous/alternate addresses removed during this edit session. Sent to the
   * API as `deleted: { previous_address: [...], alternate_address: [...] }` (Angular
   * `deleted_data` parity — the shortened array alone does NOT delete the rows server-side).
   * Cleared whenever the matching saved data re-seeds (i.e. after a successful save + refetch).
   */
  const [deletedPreviousAddressIds, setDeletedPreviousAddressIds] = useState<
    Array<number | string>
  >([]);
  const [deletedAlternateAddressIds, setDeletedAlternateAddressIds] = useState<
    Array<number | string>
  >([]);

  const previousAddressSeedKey = useMemo(
    () => JSON.stringify(applicationData?.previous_address ?? []),
    [applicationData?.previous_address],
  );

  const alternateAddressSeedKey = useMemo(
    () => JSON.stringify(applicationData?.alternate_address ?? []),
    [applicationData?.alternate_address],
  );

  const pipelineOptions: ApplyValidateAddressOptions = useMemo(
    () => ({
      seedFromApi: normalizePreviousAddressRows(
        (applicationData?.previous_address ?? []) as PreviousAddressRow[],
      ).map(mapApiAddressToPreviousAddressRow),
    }),
    [previousAddressSeedKey],
  );

  const syncAddressPipeline = (
    currentAddr: PreviousAddressRow,
    prevForms: PreviousAddressRow[],
  ): { current: PreviousAddressRow | null; previous: PreviousAddressRow[] } => {
    return runCurrentAddressAddressPipeline(currentAddr, prevForms, pipelineOptions);
  };

  const applyPipelineToState = (
    currentAddr: PreviousAddressRow,
    prevForms: PreviousAddressRow[] = previousAddressForms,
  ) => {
    const resolvedPrev = resolvePreviousFormsForPipeline(
      prevForms,
      pipelineOptions.seedFromApi as PreviousAddressRow[] | undefined,
    );
    const { current, previous } = syncAddressPipeline(currentAddr, resolvedPrev);
    const nextCurrent = current ?? currentAddr;
    const nextPrevious = normalizePreviousAddressRows(previous);
    const prevChanged =
      nextPrevious.length !== previousAddressForms.length ||
      nextPrevious.some((row, i) => row !== previousAddressForms[i]);
    const currentChanged = currentAddressForm !== nextCurrent;

    if (currentChanged) setCurrentAddressForm(nextCurrent);
    setPreviousAddressForms(nextPrevious);
    if (currentChanged || prevChanged) {
      sendFormDataToParent(formData, nextCurrent, nextPrevious, alternateAddressForms);
    }
    return { current: nextCurrent, previous: nextPrevious };
  };

  // Seed edit state from API + validateAddress (re-runs after save/import).
  useEffect(() => {
    setDeletedPreviousAddressIds([]);
    if (!isEdit) {
      setPreviousAddressForms([]);
      setCurrentAddressForm(null);
      return;
    }
    if (!currentAddress) return;

    const apiPrevious = normalizePreviousAddressRows(
      (applicationData?.previous_address ?? []) as PreviousAddressRow[],
    ).map(mapApiAddressToPreviousAddressRow);

    const currentSeed = (currentAddressForm ?? currentAddress) as PreviousAddressRow;
    const { current, previous } = syncAddressPipeline(currentSeed, apiPrevious);
    const nextCurrent = (current ?? currentSeed) as PreviousAddressRow;
    const nextPrevious = normalizePreviousAddressRows(previous);

    setCurrentAddressForm(nextCurrent);
    setPreviousAddressForms(nextPrevious);
    sendFormDataToParent(formData, nextCurrent, nextPrevious, alternateAddressForms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, applicationData?.applicant_id, previousAddressSeedKey]);

  // Re-seed alternates after save/refetch so ra_id flows back (Angular setAlternateAddress).
  useEffect(() => {
    setAlternateAddressForms(mapAlternateAddressesToForms(alternateAddresses));
    setDeletedAlternateAddressIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData?.applicant_id, alternateAddressSeedKey]);

  // Update form data when applicationData changes or edit mode is toggled
  useEffect(() => {
    if (applicationData) {
      const personal = aboutYourselfPersonalDisplay(applicationData);
      setFormData({
        first_name: personal.first_name || "",
        middle_name: personal.middle_name || "",
        last_name: personal.last_name || "",
        suffix: personal.suffix || "",
        mobile_no: personal.mobile_no || "",
        shared_phone_no: personal.shared_phone_no === "1",
        communication_preference: personal.communication_preference || "Text Msg",
        birthdate: parseDate(personal.birthdate),
        security_number: personal.security_number || "",
        email: personal.email || "",
      });
    }
  }, [applicationData, isEdit]);

  useEffect(() => {
    if (!isEdit) {
      setPersonalFieldsDirty({});
      setCommunicationPreferenceHelpOpen(false);
    }
  }, [isEdit]);

  useEffect(() => {
    setPersonalFieldsDirty({});
  }, [applicationData?.applicant_id]);

  useEffect(() => {
    if (applicationData?.past_customer !== "1") {
      setPreviousAppsCount(null);
      return;
    }

    let cancelled = false;
    void getPreviousApplicationList(applicantId)
      .then((res) => {
        if (!cancelled) {
          setPreviousAppsCount(Array.isArray(res.data) ? res.data.length : 0);
        }
      })
      .catch(() => {
        if (!cancelled) setPreviousAppsCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [applicationData?.past_customer, applicantId]);

  // Angular `checkOutsideUSAddress`: clear opt-out when checkbox should be hidden.
  useEffect(() => {
    if (!isEdit) return;
    const currentAddr = currentAddressForm || currentAddress;
    const prevAddrs =
      previousAddressForms.length > 0
        ? previousAddressForms
        : previousAddresses;
    if (!hasNonUSAddressInHistory(currentAddr, prevAddrs) && noAlternateAddressUS) {
      setNoAlternateAddressUS(false);
      sendFormDataToParent(
        { ...formData, non_us_address: "" },
        (currentAddr as PreviousAddressRow) ?? null,
        prevAddrs as PreviousAddressRow[],
        alternateAddressForms,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAddressForm,
    currentAddress,
    previousAddressForms,
    previousAddresses,
    isEdit,
    noAlternateAddressUS,
  ]);

  // Send form data to parent; `deleted` carries removed ra_ids for server deletes (Angular).
  const sendFormDataToParent = (
    personalInfo: AboutYourselfPersonalFormData & { non_us_address?: string },
    currentAddr: PreviousAddressRow | null = currentAddressForm,
    previousAddrs: PreviousAddressRow[] = previousAddressForms,
    alternateAddrs: AboutYourselfAlternateAddressFormData[] = alternateAddressForms,
    deleted: {
      previous_address?: Array<number | string>;
      alternate_address?: Array<number | string>;
    } = {
      previous_address: deletedPreviousAddressIds,
      alternate_address: deletedAlternateAddressIds,
    },
  ) => {
    onFormChange?.(
      buildAboutYourselfFormChangePayload(
        personalInfo,
        currentAddr,
        previousAddrs,
        alternateAddrs,
        deleted,
      ),
    );
  };

  // Angular parity: previous-address rows are appended automatically by the
  // `validateAddress()`-style effect above; no manual handler is needed.

  const handleInputChange = (field: string, value: string | boolean) => {
    if (personalFieldsLocked) {
      return;
    }
    setPersonalFieldsDirty((d) => ({ ...d, [field]: true }));
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    sendFormDataToParent(
      newFormData,
      currentAddressForm,
      previousAddressForms,
      alternateAddressForms,
    );
  };

  const handleCurrentAddressChange = (addressData: AboutYourselfAddressEditFormData) => {
    applyPipelineToState(addressData as PreviousAddressRow, previousAddressForms);
  };

  const handlePreviousAddressChange = (
    index: number,
    addressData: AboutYourselfAddressEditFormData,
  ) => {
    setPreviousAddressForms((prev) => {
      const merged = normalizePreviousAddressRows(prev);
      while (merged.length <= index) {
        merged.push(createEmptyPreviousAddressRow());
      }
      const existing = merged[index] ?? createEmptyPreviousAddressRow();
      const ad = {
        ...(existing.address_data ?? {}),
        ...(addressData?.address_data ?? {}),
      };
      const moveIn =
        addressData?.moveIn_date ??
        addressData?.address_data?.moveIn_date ??
        existing.moveIn_date ??
        ad.moveIn_date;
      if (moveIn) ad.moveIn_date = moveIn;

      merged[index] = {
        ...existing,
        ...addressData,
        address_type: addressData?.address_type ?? existing.address_type ?? "RENT",
        address_data: ad,
      };

      const currentAddr = (currentAddressForm ?? currentAddress) as PreviousAddressRow;
      const currentMoveIn = getAddressMoveInYmd(currentAddr);
      let rows = merged;
      if (currentMoveIn) {
        rows = applyPreviousAddressMonths(rows, currentMoveIn);
      }
      const previous = normalizePreviousAddressRows(
        applyValidateAddress(currentAddr, rows, pipelineOptions),
      );
      sendFormDataToParent(formData, currentAddressForm, previous, alternateAddressForms);
      return previous;
    });
  };

  // Alternate address handlers
  const handleAlternateAddressChange = (
    index: number,
    addressData: AboutYourselfAlternateAddressFormData,
  ) => {
    const newAlternateAddresses = [...alternateAddressForms];
    newAlternateAddresses[index] = addressData;
    setAlternateAddressForms(newAlternateAddresses);
    sendFormDataToParent(
      formData,
      currentAddressForm,
      previousAddressForms,
      newAlternateAddresses,
    );
  };

  const handleAddAlternateAddress = () => {
    const newAddress = createEmptyAlternateAddressForm();
    const newAlternateAddresses = [...alternateAddressForms, newAddress];
    setAlternateAddressForms(newAlternateAddresses);
    sendFormDataToParent(
      formData,
      currentAddressForm,
      previousAddressForms,
      newAlternateAddresses,
    );
  };

  // Angular setDeletedID — only saved rows (truthy ra_id) need server delete.
  const appendDeletedRaId = (
    list: Array<number | string>,
    raId: number | string | null | undefined,
  ): Array<number | string> =>
    raId !== undefined && raId !== null && String(raId).trim() !== ""
      ? [...list, raId]
      : list;

  const handleRemoveAlternateAddress = (index: number) => {
    const removed = alternateAddressForms[index];
    const newAlternateAddresses = alternateAddressForms.filter(
      (_, i) => i !== index,
    );
    setAlternateAddressForms(newAlternateAddresses);

    const newDeletedAlternateIds = appendDeletedRaId(
      deletedAlternateAddressIds,
      removed?.ra_id,
    );
    if (newDeletedAlternateIds !== deletedAlternateAddressIds) {
      setDeletedAlternateAddressIds(newDeletedAlternateIds);
    }

    sendFormDataToParent(
      formData,
      currentAddressForm,
      previousAddressForms,
      newAlternateAddresses,
      {
        previous_address: deletedPreviousAddressIds,
        alternate_address: newDeletedAlternateIds,
      },
    );
  };

  const handleRemovePreviousAddress = (index: number) => {
    const removed = previousAddressForms[index];
    const filtered = previousAddressForms.filter((_, i) => i !== index);
    const currentAddr = (currentAddressForm ?? currentAddress) as PreviousAddressRow;
    const { previous: next } = syncAddressPipeline(currentAddr, filtered);
    setPreviousAddressForms(next);

    const newDeletedPreviousIds = appendDeletedRaId(
      deletedPreviousAddressIds,
      removed?.ra_id,
    );
    if (newDeletedPreviousIds !== deletedPreviousAddressIds) {
      setDeletedPreviousAddressIds(newDeletedPreviousIds);
    }

    sendFormDataToParent(formData, currentAddressForm, next, alternateAddressForms, {
      previous_address: newDeletedPreviousIds,
      alternate_address: deletedAlternateAddressIds,
    });
  };

  // Check if alternate-address opt-out checkbox should show (Angular `nonUSAddress`).
  const hasNonUSAddress = (): boolean => {
    const currentAddr = currentAddressForm || currentAddress;
    const prevAddrs =
      previousAddressForms.length > 0
        ? previousAddressForms
        : previousAddresses;
    return hasNonUSAddressInHistory(currentAddr, prevAddrs);
  };

  // Handle no alternate address checkbox change
  const handleNoAlternateAddressChange = (checked: boolean) => {
    setNoAlternateAddressUS(checked);
    sendFormDataToParent(
      { ...formData, non_us_address: checked ? "1" : "" },
      currentAddressForm,
      previousAddressForms,
      alternateAddressForms,
    );
  };

  return isEdit ? (
    <AboutYourselfEditView
      view={view}
      applicationAllData={applicationAllData}
      applicationData={applicationData}
      applicantId={applicantId}
      formData={formData}
      currentAddress={currentAddress}
      currentAddressForm={currentAddressForm}
      previousAddressForms={previousAddressForms}
      alternateAddressForms={alternateAddressForms}
      exposeInlineValidation={exposeInlineValidation}
      personalFieldsLocked={personalFieldsLocked}
      personalFieldsDirty={personalFieldsDirty}
      communicationPreferenceHelpOpen={communicationPreferenceHelpOpen}
      setCommunicationPreferenceHelpOpen={setCommunicationPreferenceHelpOpen}
      sendingLink={sendingLink}
      noAlternateAddressUS={noAlternateAddressUS}
      pendingVerifyEmail={pendingVerifyEmail}
      pendingSendLink={pendingSendLink}
      onInputChange={handleInputChange}
      onSendApplicationLink={handleSendApplicationLink}
      onCurrentAddressChange={handleCurrentAddressChange}
      onPreviousAddressChange={handlePreviousAddressChange}
      onRemovePreviousAddress={handleRemovePreviousAddress}
      onAlternateAddressChange={handleAlternateAddressChange}
      onAddAlternateAddress={handleAddAlternateAddress}
      onRemoveAlternateAddress={handleRemoveAlternateAddress}
      onNoAlternateAddressChange={handleNoAlternateAddressChange}
      hasNonUSAddress={hasNonUSAddress}
      getAddressMoveInYmd={getAddressMoveInYmd}
      onVerify={() => void performVerifyEmail()}
      onCancelVerify={() => setPendingVerifyEmail(null)}
      onSendApplicationLinkConfirm={() => void performSendApplicationLink()}
      onCancelSendApplicationLink={() => setPendingSendLink(null)}
    />
  ) : (
    <AboutYourselfReadOnlyView
      view={view}
      applicationAllData={applicationAllData}
      applicationData={applicationDataForPersonal}
      applicantId={applicantId}
      userData={personalFieldsLocked ? snapshotUserData : userData}
      currentAddress={currentAddress}
      previousAddresses={previousAddresses}
      alternateAddresses={alternateAddresses}
      showPrimaryApplicantActions={showPrimaryApplicantActions}
      showResidentButton={showResidentButton}
      showDefaultButton={showDefaultButton}
      rentalStatusLine={rentalStatusLine}
      previousAppsOpen={previousAppsOpen}
      previousAppsCount={previousAppsCount}
      pendingVerifyEmail={pendingVerifyEmail}
      pendingSendLink={pendingSendLink}
      canValidateEmail={canValidateEmail}
      canViewAddressOnMap={canViewAddressOnMap}
      verifyingEmail={verifyingEmail}
      onSetPreviousAppsOpen={setPreviousAppsOpen}
      onOpenResident={handleOpenResident}
      onOpenDefault={handleOpenDefault}
      onVerifyEmail={handleVerifyEmail}
      onVerify={() => void performVerifyEmail()}
      onCancelVerify={() => setPendingVerifyEmail(null)}
      onSendApplicationLink={() => void performSendApplicationLink()}
      onCancelSendApplicationLink={() => setPendingSendLink(null)}
    />
  );
}
